import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { Buildings } from '@phosphor-icons/react';

/**
 * Free-text developer-name input with self-learning autocomplete.
 *
 * Hits GET /api/properties/developer-suggestions?q=<prefix> on every
 * keystroke (with a 200ms debounce) and lists the top 8 distinct
 * developer names sorted by popularity. The user can type anything they
 * like — the suggestions are a hint, never a constraint. As the
 * platform's property count grows, the autocomplete naturally covers
 * more UK developers without us having to maintain a hardcoded list.
 *
 * Props:
 *   value, onChange, placeholder
 *   testId        — root data-testid (input gets `${testId}-input`)
 *   className     — passed through to the input for layout overrides
 */
export default function DeveloperNameInput({
    value, onChange, placeholder = "e.g. Persimmon, Bovis, Barratt — optional",
    testId = 'developer-name', className = '',
}) {
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [highlight, setHighlight] = useState(-1);
    const debounceRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        // Don't bother hitting the network for trivially short prefixes —
        // they'd return half the database and aren't useful as a hint.
        if (!value || value.length < 1) { setSuggestions([]); return; }
        debounceRef.current = setTimeout(async () => {
            try {
                const res = await axios.get(
                    `${API}/properties/developer-suggestions`,
                    { params: { q: value }, withCredentials: true },
                );
                const list = (res.data?.suggestions || [])
                    .filter(s => (s.name || '').toLowerCase() !== (value || '').toLowerCase());
                setSuggestions(list);
                setHighlight(-1);
            } catch { /* offline / 402 / etc — autocomplete is a nicety, not critical */ }
        }, 200);
        return () => clearTimeout(debounceRef.current);
    }, [value]);

    // Click-outside to close.
    useEffect(() => {
        const onDoc = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);

    const showList = open && suggestions.length > 0;

    const onKey = (e) => {
        if (!showList) return;
        if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, suggestions.length - 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, -1)); }
        else if (e.key === 'Enter' && highlight >= 0) {
            e.preventDefault();
            onChange(suggestions[highlight].name);
            setOpen(false);
        } else if (e.key === 'Escape') {
            setOpen(false);
        }
    };

    return (
        <div ref={containerRef} className="relative" data-testid={testId}>
            <div className="relative">
                <Buildings size={16} weight="duotone" color="#1FA5CF"
                    className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                    data-testid={`${testId}-input`}
                    value={value}
                    onChange={(e) => { onChange(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={onKey}
                    placeholder={placeholder}
                    className={`w-full pl-9 pr-3 py-3 border border-[#E4E4E7] focus:border-[#18181B] outline-none text-sm ${className}`}
                />
            </div>
            {showList && (
                <ul
                    data-testid={`${testId}-suggestions`}
                    className="absolute z-20 left-0 right-0 mt-1 bg-white border border-[#18181B] max-h-56 overflow-y-auto shadow-lg"
                >
                    {suggestions.map((s, i) => (
                        <li key={s.name}>
                            <button
                                type="button"
                                onMouseDown={(e) => { e.preventDefault(); onChange(s.name); setOpen(false); }}
                                onMouseEnter={() => setHighlight(i)}
                                data-testid={`${testId}-suggestion-${i}`}
                                className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-3 ${i === highlight ? 'bg-[#E8F5FB]' : 'hover:bg-[#F4F4F5]'}`}
                            >
                                <span className="truncate">{s.name}</span>
                                <span className="text-[10px] font-mono uppercase tracking-wider text-[#A1A1AA] flex-shrink-0">
                                    {s.count} home{s.count === 1 ? '' : 's'}
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
